import React, { useRef } from 'react';
import Button from '@ingka/button';
import modulationArrow from '@ingka/ssr-icon/paths/arrow-left-arrow-right';
import warningTriangle from '@ingka/ssr-icon/paths/warning-triangle';
import type { DummyCoworker } from 'types/scenario';
import { useTranslation } from 'react-i18next';
import Tooltip from '@ingka/tooltip';
import useData from 'hooks/useData';
import type { Coworker } from 'types/coworker';
import Text from '@ingka/text';
import useAuth from 'hooks/useAuth';
import classes from './CoworkerNameCell.module.scss';

const CoworkerNameCell = ({
    coworker,
    handleClickCoworker,
    isActive,
    showModulationIcon,
}: {
    coworker: Coworker | DummyCoworker;
    handleClickCoworker: () => void;
    isActive: boolean;
    showModulationIcon: boolean;
}) => {
    const { access } = useAuth();
    const { currentScenario } = useData();
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    return (
        <div ref={ref} className={`${coworker.isDummy ? classes['coworker-name--dummy'] : ''}`}>
            {isActive ? (
                <div className={classes['coworker-name']} data-testid="user-details-trigger">
                    <div
                        className={classes['table-row__employee']}
                        onClick={handleClickCoworker}
                        onKeyDown={handleClickCoworker}
                        data-testid="user-details-legalFullName"
                    >
                        <Text textStyle="Heading.XS">{coworker.legalFullName}</Text>
                        <Text textStyle="Body.M" data-testid="user-details-personID">{coworker.isDummy ? '' : coworker.personId}</Text>
                    </div>
                    <div className={`${classes['coworker-name__button']}`} data-testid="user-modulation-icon">
                        {showModulationIcon && access?.features?.modulation && (
                            <Tooltip
                                describedById="user-modulation-tooltip-icon"
                                tooltipText={!currentScenario
                                    ? `${t('MODULATION_ELIGIBLE_COWORKER')}. ${t('SELECT_A_SCENARIO')}`
                                    : t('MODULATION_ELIGIBLE_COWORKER')}
                                position="trailing"
                                data-testid="user-modulation-tooltip-icon"
                            >
                                <Button
                                    data-testid="user-modulation-button"
                                    iconPosition="trailing"
                                    iconOnly
                                    size="small"
                                    ssrIcon={modulationArrow}
                                    type="tertiary"
                                    className={`${classes['coworker-name__button--active']} ${currentScenario
                                        ? '' : classes['coworker-name__button--inactive']}`}
                                />
                            </Tooltip>
                        )}
                    </div>
                </div>
            ) : (
                <Tooltip
                    describedById="user-deactivated-tooltip"
                    tooltipText={`${t('DEACTIVATED_COWORKER_WARNING_SHORT')}.`}
                    position="trailing"
                    data-testid="user-deactivated-tooltip"
                >
                    <div
                        className={classes['coworker-name--deactivated']}
                        data-testid="user-deactivated-trigger"
                        onClick={handleClickCoworker}
                        onKeyDown={handleClickCoworker}
                    >
                        <div className={classes['coworker-name--deactivated--button']}>
                            <Button
                                data-testid="user-deactivated-icon"
                                ssrIcon={warningTriangle}
                                iconOnly
                                size="small"
                                iconPosition="trailing"
                                type="tertiary"
                                className={classes['coworker-name--deactivated--button--warning']}
                            />
                        </div>
                        <div data-testid="user-deactivated-fullName">
                            <Text
                                textStyle="Heading.XS"
                                data-testid="user-deactivated-name"
                                className={`${classes['table-row__employee']} ${currentScenario
                                    ? '' : classes['coworker-name--deactivated']}`}
                            >
                                {coworker.legalFullName}
                            </Text>
                            <Text textStyle="Body.S" data-testid="user-deactivated-personID">
                                {coworker.isDummy ? '' : coworker.personId}
                            </Text>
                        </div>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default CoworkerNameCell;
